
import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend, LabelList } from "recharts";
import { channelLabels, getBarColor } from "./AnalyticsUtils";
import { AutomatedMessage } from "../message-utils/types";

interface AnalyticsChartProps {
  message: AutomatedMessage;
}

const AnalyticsChart = ({ message }: AnalyticsChartProps) => {
  // Transform metrics data for chart
  const getMetricsData = () => {
    return [
      {
        name: "Mensagens enviadas",
        ...Object.entries(message.metrics.byChannel).reduce((acc, [channel, metrics]) => {
          acc[channelLabels[channel as keyof typeof channelLabels]] = metrics.sentCount;
          return acc;
        }, {} as Record<string, number>)
      },
      {
        name: "Taxa de abertura",
        ...Object.entries(message.metrics.byChannel).reduce((acc, [channel, metrics]) => {
          acc[channelLabels[channel as keyof typeof channelLabels]] = metrics.openRate;
          return acc;
        }, {} as Record<string, number>)
      },
      {
        name: "Taxa de clique",
        ...Object.entries(message.metrics.byChannel).reduce((acc, [channel, metrics]) => {
          acc[channelLabels[channel as keyof typeof channelLabels]] = metrics.clickRate;
          return acc;
        }, {} as Record<string, number>)
      },
      {
        name: "Taxa de conversão",
        ...Object.entries(message.metrics.byChannel).reduce((acc, [channel, metrics]) => {
          acc[channelLabels[channel as keyof typeof channelLabels]] = metrics.conversionRate;
          return acc;
        }, {} as Record<string, number>)
      }
    ];
  };

  // Custom tooltip formatter to format values based on metric type
  const formatTooltipValue = (value: number, name: string, entry: any) => {
    if (entry.dataKey === "Mensagens enviadas") {
      return [`${value}`, ""];
    }
    return [`${value}%`, ""];
  };

  // Custom label formatter for bar labels
  const renderCustomBarLabel = (props: any) => {
    const { x, y, width, value, index } = props;
    
    const formatValue = (val: number, dataIndex: number) => {
      if (dataIndex === 0) return val;
      return `${val}%`;
    };
    
    return (
      <text 
        x={x + width / 2} 
        y={y - 10} 
        fill="#6b7280" 
        textAnchor="middle" 
        fontSize={12}
      >
        {formatValue(value, index)}
      </text>
    );
  };

  return (
    <div className="h-[350px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={getMetricsData()}
          margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
          className="[&_.recharts-cartesian-grid-horizontal]:stroke-gray-100 [&_.recharts-cartesian-grid-horizontal]:dark:stroke-gray-800 [&_.recharts-cartesian-grid-vertical]:stroke-gray-100 [&_.recharts-cartesian-grid-vertical]:dark:stroke-gray-800"
        >
          <defs>
            {message.channels.includes('email') && (
              <linearGradient id="emailBarGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#58aba8" stopOpacity={0.9}/>
                <stop offset="95%" stopColor="#58aba8" stopOpacity={0.7}/>
              </linearGradient>
            )}
            {message.channels.includes('whatsapp') && (
              <linearGradient id="whatsappBarGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#25D366" stopOpacity={0.9}/>
                <stop offset="95%" stopColor="#25D366" stopOpacity={0.7}/>
              </linearGradient>
            )}
            {message.channels.includes('sms') && (
              <linearGradient id="smsBarGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF6B6B" stopOpacity={0.9}/>
                <stop offset="95%" stopColor="#FF6B6B" stopOpacity={0.7}/>
              </linearGradient>
            )}
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="stroke-gray-100 dark:stroke-gray-800" opacity={0.5} />
          <XAxis 
            dataKey="name" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'currentColor' }}
            className="text-gray-500 dark:text-gray-400"
            dy={10}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: 'currentColor' }}
            className="text-gray-500 dark:text-gray-400"
          />
          <Tooltip 
            formatter={formatTooltipValue} 
            cursor={{fill: 'rgba(200, 200, 200, 0.1)'}}
          />
          {message.channels.includes('email') && (
            <Bar 
              dataKey="Email" 
              fill="url(#emailBarGradient)" 
              name="Email"
              radius={[4, 4, 0, 0]}
              className="hover:opacity-90 transition-opacity"
            >
              <LabelList dataKey="Email" content={renderCustomBarLabel} />
            </Bar>
          )}
          {message.channels.includes('whatsapp') && (
            <Bar 
              dataKey="WhatsApp" 
              fill="url(#whatsappBarGradient)" 
              name="WhatsApp"
              radius={[4, 4, 0, 0]}
              className="hover:opacity-90 transition-opacity"
            >
              <LabelList dataKey="WhatsApp" content={renderCustomBarLabel} />
            </Bar>
          )}
          {message.channels.includes('sms') && (
            <Bar 
              dataKey="SMS" 
              fill="url(#smsBarGradient)" 
              name="SMS"
              radius={[4, 4, 0, 0]}
              className="hover:opacity-90 transition-opacity"
            >
              <LabelList dataKey="SMS" content={renderCustomBarLabel} />
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AnalyticsChart;
